import React, { useEffect, useState,useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Typography, Button, Input } from "antd";
import axios from "../axiosSetup";
import Breadcrumb from "../components/Breadcrumbs";
import { formatMoney } from "../utils/formatMoney";
import "./MarginCalculator.css";
import { useOverflowTooltip } from "../hooks/useOverflowTooltip";
import CustomTooltip from "../components/CustomTooltip";

const cleanPercent = (val) => Number(String(val).replace("%", "").trim()) || 0;

const getDiscountedRrp = (product) => {
  const rrp = Number(product?.rrp || 0);
  const discount = Number(product?.discount || 0);

  if ((product?.discountType || "").toLowerCase() === "percentage") {
    return Math.max(rrp - (rrp * discount) / 100, 0);
  }

  return Math.max(rrp - discount, 0);
};

const evaluateMarginFormula = (formula, values) => {
  if (!formula || typeof formula !== "string") return 0;

  const normalized = formula.toLowerCase().replace(/\s+/g, "");
  const replacements = {
    rrp: Number(values.rrp || 0),
    landedcost: Number(values.landedCost || 0),
    landcost: Number(values.landedCost || 0),
    costprice: Number(values.costPrice || 0),
    customerprice: Number(values.customerPrice || 0),
  };

  let expression = normalized;
  Object.entries(replacements).forEach(([key, value]) => {
    expression = expression.replace(new RegExp(key, "g"), String(value));
  });

  if (!/^[0-9+\-*/().]+$/.test(expression)) {
    return 0;
  }

  try {
    const result = Function(`"use strict"; return (${expression});`)();
    return Number.isFinite(result) ? result : 0;
  } catch {
    return 0;
  }
};

const calculateLineValues = (product, qty, extraPercent = 0) => {
  const quantityValue = Number(qty) || 0;
  const baseRrpAfterDiscount = getDiscountedRrp(product);
  const extra = cleanPercent(extraPercent);
  const finalCustomerPrice = Math.max(
    baseRrpAfterDiscount - (baseRrpAfterDiscount * extra) / 100,
    0
  );

  let grossMarginPerUnit = 0;
  let grossMarginPercent = 0;

  if ((product?.marginType || "").toLowerCase() === "percentage") {
    grossMarginPercent = Number(product?.marginPercentage || 0);
    grossMarginPerUnit = (finalCustomerPrice * grossMarginPercent) / 100;
  } else {
    grossMarginPerUnit = evaluateMarginFormula(product?.marginFormula, {
      rrp: finalCustomerPrice,
      landedCost: Number(product?.landedCost || 0),
      costPrice: Number(product?.costPrice || 0),
      customerPrice: finalCustomerPrice,
    });
    grossMarginPercent =
      finalCustomerPrice > 0
        ? (grossMarginPerUnit * 100) / finalCustomerPrice
        : 0;
  }

  return {
    customerPrice: finalCustomerPrice,
    totalPrice: finalCustomerPrice * quantityValue,
    grossMargin: grossMarginPerUnit * quantityValue,
    grossMarginPercent,
  };
};

const HeaderCell = ({ text }) => {
  const { ref, showTooltip } = useOverflowTooltip(text);

  return (
    <CustomTooltip
      message={text}
      placement="top"
      color="#4A1476"
      show={showTooltip}
    >
      <span
        ref={ref}
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "inline-block",
          maxWidth: "100%",
        }}
      >
        {text}
      </span>
    </CustomTooltip>
  );
};

const OverflowText = ({ text }) => {
  const { ref, showTooltip } = useOverflowTooltip(text);

  return (
    <CustomTooltip
      message={text}
      placement="top"
      color="#4A1476"
      show={showTooltip}
    >
      <span
        ref={ref}
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "inline-block",
          maxWidth: "220px",   // adjust based on your layout
          verticalAlign: "bottom",
        }}
      >
        {text || "-"}
      </span>
    </CustomTooltip>
  );
};


const { Title, Text } = Typography;
const renderMoneyValue = (value, currency) =>
  value === null || value === undefined || value === ""
    ? "-"
    : formatMoney(value, currency);

const ViewQuote = () => {
  const { id } = useParams();
  const navigate = useNavigate();
    const fetchedRef = React.useRef(false);
  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState(null);
  const [addedProducts, setAddedProducts] = useState([]);
  const [summary, setSummary] = useState({
  hardwareTotal: 0,
  softwareTotal: 0,
  overallTotal: 0,
  grossMargin: 0,
  grossMarginPercent: 0,
  grossMarginBeforeTax: 0,
  grossMarginPercentBeforeTax: 0,
});

  const load = async () => {
  const res = await axios.get(`/margin-calculator/${id}`);

  const q = res.data;
  setQuote(q);

  const mapped = q.products.map((p) => {
  const beforeValues = calculateLineValues(p, p.quantity, 0);
  const afterValues = calculateLineValues(p, p.quantity, p.onTopStdPercent ?? 0);

  // Gross margin after customer discount (BEFORE onTopStdPercent) - margin-aware via calculateLineValues
  const beforeCustomerPrice = Number(beforeValues.customerPrice || 0);
  const beforeGrossMargin = beforeValues.grossMargin;
  const beforeGrossMarginPercent = beforeValues.grossMarginPercent;

  return {
    productType: p.productType || "",
    productCode: p.productCode,
    costPrice: p.costPrice,
    rrp: p.rrp,
    quantity: p.quantity,
    discount: p.discount,
    discountType: p.discountType,
    extraDiscount: p.onTopStdPercent ?? 0,
    marginType: p.marginType,
    marginFormula: p.marginFormula,
    marginPercentage: p.marginPercentage ?? p.grossMarginPercentage,
    landedCost: p.landedCost,

    // BEFORE on-top values (after customer discount only)
    beforeCustomerPrice: beforeValues.customerPrice,
    beforeTotal: beforeValues.totalPrice,
    beforeGrossMargin: beforeValues.grossMargin,
    beforeGrossMarginPercent: beforeValues.grossMarginPercent,

    // Use margin-aware beforeValues for "after customer discount" columns
    grossMarginAfterDiscount: beforeGrossMargin,
    grossMarginPercentAfterDiscount: beforeGrossMarginPercent,

    // AFTER on-top values prefer backend direct response.
    customerPrice: p.customerPrice ?? afterValues.customerPrice,
    totalCustomerPrice: p.total ?? afterValues.totalPrice,
    grossMargin: p.grossMargin ?? afterValues.grossMargin,
    grossMarginPercent:
      p.grossMarginPercentage ?? p.grossMarginPercent ?? afterValues.grossMarginPercent,

    currency: q.currency,
  };

});


  setAddedProducts(mapped);

  setSummary({
  hardwareTotal: q.hardwareTotal || 0,
  softwareTotal: q.softwareTotal || 0,
  overallTotal: q.totalPrice || 0,
  grossMargin: q.grossMargin || 0,
  grossMarginPercent: q.grossMarginPercent || 0,
  grossMarginBeforeTax: q.totals?.grossMarginBeforeTax || 0,
  grossMarginPercentBeforeTax: q.totals?.grossMarginPercentBeforeTax || 0,
});

  setLoading(false);
};

useEffect(() => {
  if (fetchedRef.current) return;
  fetchedRef.current = true;

  load();
}, [id]);
  if (loading) return null;

  const summaryCurrency = quote.currency;
  const readOnlyStyle = {
  display: "block",
  // background: "#E5D8ED",
  // border: "1px solid #C76CFF",
  // borderRadius: "8px",
  height: "38px",
  padding: "8px 12px",
  color: "#9A49CD",
  lineHeight: "20px",
};

  return (
    <div className="calculate-margin-container">
      <Breadcrumb />

      <Card className="quote-info-card" bordered={false}>
        {/* <Title level={5} style={{color:"#7509B8"}}>Quote Information</Title> */}

        <div className="quote-info-grid">
         <div className="form-field">
  <Text className="label">Customer Name</Text>
  <Text>
    <OverflowText text={quote.customerName} />
  </Text>
</div>

<div className="form-field">
  <Text className="label">Calculation Name</Text>
  <Text >{quote.quoteName || "-"}</Text>
</div>

<div className="form-field">
  <Text className="label">Number of Stores</Text>
  <Text >{quote.numberOfStores}</Text>
</div>

<div className="form-field">
  <Text className="label">Additional Bulk Discount</Text>
  <Text >{quote.additionalBulkDiscount}%</Text>
</div>

        </div>
      </Card>

      <Card className="products-card" bordered={false}>
  <div className="products-header">
    <Title level={5} style={{color:"#7509B8"}}>Products</Title>
  </div>

  <div className="products-content">
    {addedProducts.length > 0 && (
      <div className="cm-product-table-wrapper">
        <div className="cm-product-table-scroll">
          <table className="cm-product-table wide-table">
            <thead>
  <tr>
    <th><HeaderCell text="Type of Product" /></th>
    <th><HeaderCell text="Vocovo Product Code" /></th>
    {/* <th><HeaderCell text="Item Type" /></th> */}
    <th><HeaderCell text="Landed Cost" /></th>
    <th><HeaderCell text="RRP" /></th>
    <th><HeaderCell text="Quantity" /></th>
    <th><HeaderCell text="Customer Discount" /></th>
    {/* <th><HeaderCell text="Customer Price" /></th> */}
    {/* <th><HeaderCell text="Total Price" /></th> */}
    {/* <th><HeaderCell text="Gross margin after customer discount applied" /></th> */}
    {/* <th><HeaderCell text="Gross margin % after customer discount applied" /></th> */}
    <th><HeaderCell text="On Top of std Discount %" /></th>
    <th><HeaderCell text="Customer Price " /></th>
    <th><HeaderCell text="Total Price" /></th>
    <th><HeaderCell text="Gross Margin" /></th>
    <th><HeaderCell text="Gross Margin %" /></th>
  </tr>
</thead>


            <tbody>
              {addedProducts.map((p, idx) => (
                <tr key={idx}>
                  <td>{p.productType}</td>
                  <td><OverflowText text={p.productCode} /></td>
                  {/* <td>{p.itemType}</td> */}
                  <td>{renderMoneyValue(p.landedCost, p.currency)}</td>
<td>{renderMoneyValue(p.rrp, p.currency)}</td>
                  <td>{p.quantity}</td>

                  <td>
                    {p.discountType === "Percentage"
                      ? `${p.discount}%`
                      : `${p.currency}${p.discount}`}
                  </td>

                  {/* <td>{renderMoneyValue(p.beforeCustomerPrice, p.currency)}</td> */}
    {/* <td>{renderMoneyValue(p.beforeTotal, p.currency)}</td> */}
    {/* <td>{renderMoneyValue(p.grossMarginAfterDiscount, p.currency)}</td> */}
                  {/* <td>{p.grossMarginPercentAfterDiscount.toFixed(2)}%</td> */}

                  <td>{p.extraDiscount || "0"}%</td>
                  <td>{renderMoneyValue(p.customerPrice, p.currency)}</td>

                  <td>{renderMoneyValue(p.totalCustomerPrice, p.currency)}</td>
<td>{renderMoneyValue(p.grossMargin, p.currency)}</td>
                  <td>{p.grossMarginPercent.toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}
  </div>
</Card>


      <Card className="overall-summary-card" bordered={false} style={{ marginTop: 24 }}>
  <h3 style={{ color: "#7509B8" }}>Overall Summary</h3>

  {quote.taxes?.length > 0 && (
  <p style={{color: "#7509B8"}}>
    Taxes applied:&nbsp;
    {quote.taxes.map((t, i) => (
      <span key={i}>
        {t.name} ({t.percentage}%)
        {i < quote.taxes.length - 1 && ", "}
      </span>
    ))}
  </p>
)}

  <div className="mc-summary-grid">
  <div className="summary-card">
    <p>Hardware Total ({summaryCurrency})</p>
    <h4>{formatMoney(summary.hardwareTotal, summaryCurrency)}</h4>
  </div>

  <div className="summary-card">
    <p>Software Total ({summaryCurrency})</p>
    <h4>{formatMoney(summary.softwareTotal, summaryCurrency)}</h4>
  </div>

  <div className="summary-card">
    <p>Overall Total ({summaryCurrency})</p>
    <h4>{formatMoney(summary.overallTotal, summaryCurrency)}</h4>
  </div>

  {/* <div className="summary-card">
    <p>Gross Margin Before Tax ({summaryCurrency})</p>
    <h4>{summaryCurrency}{summary.grossMarginBeforeTax.toFixed(2)}</h4>
  </div>

  <div className="summary-card">
    <p>Gross Margin (%) Before Tax</p>
    <h4>{summary.grossMarginPercentBeforeTax.toFixed(2)}%</h4>
  </div> */}

  <div className="summary-card">
    <p>Overall Gross Margin ({summaryCurrency})</p>
    <h4>{formatMoney(summary.grossMargin, summaryCurrency)}</h4>
  </div>

  <div className="summary-card">
    <p>Overall Gross Margin (%) </p>
    <h4>{summary.grossMarginPercent.toFixed(2)}%</h4>
  </div>
</div>

</Card>


      <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end" }}>
        <Button onClick={() => navigate("/margin-calculator")}>
          Back
        </Button>
      </div>
    </div>
  );
};

export default ViewQuote;
